import { useState } from "react";
import * as XLSX from "xlsx";
import { ArrowUpTrayIcon, XCircleIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const champsBDD = [
  "type",   
  "nom",
  "prenom",
  "telephone",
  "email",
  "linkedin",
  "statutJuridique",
  "siret",
  "secteurActivite",
  "adresse",
  "sourceAcquisition",
  "commentaire",
];

export default function Import({ onClose }) {
  const [tablePreview, setTablePreview] = useState([]);
  const [mappings, setMappings] = useState({});
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });
  const [importedCount, setImportedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    readFile(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    readFile(file);
  };

  const readFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    setFileName(file.name);

    reader.onload = (evt) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })
      const headers = json[0];
      const rows = json.slice(1, 6);
      setTablePreview([headers, ...rows]);

      const initialMapping = {};
      headers.forEach((col, i) => {
        const normalized = col.toLowerCase().replace(/\s+/g, "");
        const champTrouvé = champsBDD.find((champ) => normalized.includes(champ)) || "";
        initialMapping[`col_${i}`] = champTrouvé;
      });      
      setMappings(initialMapping);
    };

    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      const mappedData = tablePreview.slice(1).map((row) => {
        const mappedRow = {};
        Object.entries(mappings).forEach(([colKey, champ], index) => {
          if (champ) mappedRow[champ] = row[parseInt(colKey.replace("col_", ""))] || null;
        });        
        return mappedRow;
      });

      const filtered = mappedData
        .filter((item, index, self) =>
          item.email &&
          ["Personne Physique", "Personne Morale"].includes(item.type?.trim()) &&
          !self.find((o, i) => o.email === item.email && i < index)
        );

      const res = await axios.post(
        `${BACKEND_URL}/leads/validate-import`,
        { leads: filtered }
      );

      setImportedCount(res.data.inserted);
      showToast(`✅ ${res.data.inserted} lead(s) importé(s) avec succès !`, "success");
      setTablePreview([]);
      setMappings({});
      setFileName("");
    } catch (err) {
      showToast("❌ Erreur lors de l'importation", "error");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-white p-6 rounded-lg shadow-lg w-[600px] max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <DialogTitle className="text-lg font-semibold">Importer un fichier Excel/CSV</DialogTitle>
            <button onClick={onClose}>
              <XCircleIcon className="w-6 h-6 text-gray-600 hover:text-red-600" />
            </button>
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="w-full p-6 mb-4 border-2 border-dashed border-gray-400 text-center rounded-lg bg-gray-50 cursor-pointer"
          >
            <p className="text-gray-600">
              Glissez un fichier Excel ou CSV ici
              <br />
              {fileName && <span className="text-sm text-blue-600">Fichier : {fileName}</span>}
            </p>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".xlsx, .xls, .csv"
                onChange={handleFileInput}
                className="hidden"
              />
              <div className="mt-2 text-sm text-blue-500 underline">ou cliquez ici pour choisir un fichier</div>
            </label>
          </div>

          {tablePreview.length > 0 && (
            <div className="overflow-auto">
              <table className="w-full text-sm mb-4 border">
                <thead>
                  <tr>
                    {tablePreview[0].map((col, i) => {
                      const colKey = `col_${i}`; // clé stable par index
                      return (
                        <th key={i} className="border p-1 text-left bg-gray-100">
                          {col}
                          <select
                            value={mappings[colKey] || ""}
                            onChange={(e) =>
                              setMappings({ ...mappings, [colKey]: e.target.value })
                            }
                            className="w-full mt-1 p-1 border rounded text-xs"
                          >
                            <option value="">-- Mapper à --</option>
                            {champsBDD.map((champ) => (
                              <option key={champ} value={champ}>
                                {champ}
                              </option>
                            ))}
                          </select>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {tablePreview.slice(1).map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} className="border p-1 truncate">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            onClick={handleImport}
            className="w-full py-2 mt-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
            disabled={loading || !Object.values(mappings).some((v) => v)}
          >
            <ArrowUpTrayIcon className="w-5 h-5" />
            {loading ? "Importation..." : "Lancer l'importation"}
          </button>
        </DialogPanel>
      </div>

      {/* ✅ Notification animée */}
      <AnimatePresence>
        {notification.show && (
          <motion.div
            initial={{ opacity: 0, x: 100, y: 50 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100, y: 50 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-50 w-full max-w-sm"
          >
            <div
              className={`rounded-lg shadow-lg p-4 flex items-start gap-3 border-l-4 ${
                notification.type === "success"
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <div className="shrink-0">
                {notification.type === "success" ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-500" />
                )}
              </div>
              <div className="flex-1 text-sm text-gray-700">
                <p className="font-semibold">
                  {notification.type === "success" ? "Succès" : "Erreur"}
                </p>
                <p className="text-sm">{notification.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}
